import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type { Components } from 'react-markdown';

import { cn } from '@/lib/utils';

const markdownProseClass =
  'max-w-none text-foreground';

const markdownComponents: Components = {
  h1: ({ className, ...props }) => <h1 className={cn('mb-3 mt-5 text-xl font-semibold tracking-tight text-foreground first:mt-0', className)} {...props} />,
  h2: ({ className, ...props }) => <h2 className={cn('mb-3 mt-5 text-lg font-semibold tracking-tight text-foreground first:mt-0', className)} {...props} />,
  h3: ({ className, ...props }) => <h3 className={cn('mb-2 mt-4 text-base font-semibold text-foreground first:mt-0', className)} {...props} />,
  h4: ({ className, ...props }) => <h4 className={cn('mb-2 mt-4 text-sm font-semibold text-foreground first:mt-0', className)} {...props} />,
  p: ({ className, ...props }) => <p className={cn('my-3 text-sm leading-7 text-foreground first:mt-0 last:mb-0', className)} {...props} />,
  strong: ({ className, ...props }) => <strong className={cn('font-semibold text-foreground', className)} {...props} />,
  em: ({ className, ...props }) => <em className={cn('italic text-foreground/90', className)} {...props} />,
  a: ({ className, ...props }) => <a className={cn('font-medium text-primary underline underline-offset-2', className)} {...props} />,
  ul: ({ className, ...props }) => <ul className={cn('my-3 ml-5 list-disc space-y-1.5 text-sm text-foreground', className)} {...props} />,
  ol: ({ className, ...props }) => <ol className={cn('my-3 ml-5 list-decimal space-y-1.5 text-sm text-foreground', className)} {...props} />,
  li: ({ className, ...props }) => <li className={cn('pl-1 leading-7 marker:text-foreground/70', className)} {...props} />,
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn('my-4 border-l-4 border-primary/30 bg-muted/30 px-4 py-2 text-sm italic text-foreground/80', className)}
      {...props}
    />
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = String(className || '').includes('language-');
    if (isBlock) {
      return (
        <code className={cn('font-mono text-[13px] text-foreground', className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className={cn('rounded bg-muted px-1.5 py-0.5 font-mono text-[13px] text-foreground', className)}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ className, ...props }) => (
    <pre
      className={cn('my-4 overflow-x-auto rounded-xl border border-border/60 bg-muted/50 p-4 text-[13px] leading-6 text-foreground', className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => <hr className={cn('my-5 border-border/60', className)} {...props} />,
  table: ({ className, ...props }) => (
    <div className="my-4 overflow-x-auto rounded-xl border border-border/60">
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  ),
  thead: ({ className, ...props }) => <thead className={cn('bg-muted/50', className)} {...props} />,
  th: ({ className, ...props }) => (
    <th className={cn('border-b border-border/60 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-foreground', className)} {...props} />
  ),
  td: ({ className, ...props }) => <td className={cn('border-b border-border/40 px-3 py-2 align-top text-sm text-foreground', className)} {...props} />,
};

function normalizeMarkdownContent(content: string): string {
  return content
    .replace(/\\n/g, '\n')
    .replace(/\/\/n/g, '\n')
    .replace(/\/n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
}

export function MarkdownContent({ content, className }: { content: string; className?: string }) {
  return (
    <div className={cn(markdownProseClass, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
        {normalizeMarkdownContent(content)}
      </ReactMarkdown>
    </div>
  );
}
